import React, { useEffect, useState, useMemo } from 'react';
import { Row, Col, Button, Table, Modal, Form, Card } from 'react-bootstrap';
import Sidebar from '../../components/Sidebar';
import axios from 'axios';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import toast from 'react-hot-toast';
import { Formik, Field, FieldArray, Form as FormikForm, ErrorMessage } from 'formik';
import * as Yup from 'yup';

const BASE_URL = process.env.REACT_APP_BASE_URL;

const DOSAGE_TYPES = [
  { value: 'tablet', label: 'Tablet' },
  { value: 'syrup', label: 'Syrup' },
  { value: 'capsule', label: 'Capsule' },
  { value: 'injection', label: 'Injection' },
  { value: 'other', label: 'Other' },
];
const DOSAGE_QUANTITIES = [0.5, 1, 2, 3];
const DOSAGE_FREQUENCIES = [
  { value: 'once', label: 'Once a day' },
  { value: 'twice', label: 'Twice a day' },
  { value: 'thrice', label: 'Thrice a day' },
  { value: 'every6', label: 'Every 6 hours' },
  { value: 'every8', label: 'Every 8 hours' },
  { value: 'custom', label: 'Custom' },
];

const defaultValues = {
  medicines: [
    { medicine: '', dosage: { type: '', quantity: '', frequency: '', custom: '' }, notes: '' },
  ],
};

const dosageSchema = Yup.object().shape({
  type: Yup.string().required('Type is required'),
  quantity: Yup.mixed().required('Qty is required'),
  frequency: Yup.string().required('Frequency is required'),
  custom: Yup.string().when('frequency', {
    is: 'custom',
    then: (schema) => schema.trim().required('Please specify frequency'),
    otherwise: (schema) => schema.optional(),
  }),
});

const validationSchema = Yup.object().shape({
  medicines: Yup.array()
    .of(
      Yup.object().shape({
        medicine: Yup.string().trim().required('Medicine is required'),
        dosage: dosageSchema,
        notes: Yup.string().max(200, 'Keep notes under 200 characters'),
      })
    )
    .min(1, 'Add at least one medicine'),
});

const Prescription = ({ patientId: propPatientId, appointment: propAppointment }) => {
  // patientId: from prop -> URL -> localStorage
  const urlParams = new URLSearchParams(window.location.search);
  const patientId =
    propPatientId || urlParams.get('patientId') || localStorage.getItem('patient_id') || localStorage.getItem('user_patient_id');
  const appointment = propAppointment || null;

  const [role, setRole] = useState(''); // doctor | patient
  const [prescriptions, setPrescriptions] = useState([]);
  const [hospitalName, setHospitalName] = useState('');
  const [subHospitalName, setSubHospitalName] = useState('');
  const [receiptNo, setReceiptNo] = useState('');
  const [editingId, setEditingId] = useState(null);

  const [formValues, setFormValues] = useState(defaultValues);

  // role detection
  useEffect(() => {
    const userStr = localStorage.getItem('user');
    let user = {};
    try { user = userStr ? JSON.parse(userStr) : {}; } catch { user = {}; }
    if (user?.role) {
      setRole(String(user.role).toLowerCase());
    } else if (user?.role_id) {
      setRole(user.role_id === 2 || user.role_id === 3 ? 'doctor' : 'patient');
    } else {
      setRole('');
    }
  }, []);

  // fetch prescription history
  useEffect(() => {
    if (!patientId) return;
    axios
      .get(`${BASE_URL}/api/patient/prescriptions/${patientId}`)
      .then((res) => setPrescriptions(Array.isArray(res.data) ? res.data : []))
      .catch(() => setPrescriptions([]));
  }, [patientId]);

  // fetch hospital + receipt info for print/PDF
  useEffect(() => {
    (async () => {
      const hospitalId = localStorage.getItem('hospitalId');
      const subHospitalId = localStorage.getItem('subHospitalId');
      try {
        if (hospitalId) {
          const res = await axios.get(`${BASE_URL}/api/subHospitals/getHospitals`);
          const found = Array.isArray(res.data)
            ? res.data.find((h) => String(h.id) === String(hospitalId))
            : null;
          setHospitalName(found?.name || '');
        }
      } catch { setHospitalName(''); }

      try {
        if (subHospitalId) {
          const res = await axios.get(`${BASE_URL}/api/subHospitals/getSubHospitals`);
          const found = Array.isArray(res.data)
            ? res.data.find((s) => String(s.sub_hospital_id) === String(subHospitalId))
            : null;
          setSubHospitalName(found?.sub_hospital_name || '');
        }
      } catch { setSubHospitalName(''); }

      try {
        if (patientId) {
          const res = await axios.get(`${BASE_URL}/api/patient/minimal-patient-details/${patientId}`);
          setReceiptNo(res.data?.receipt_no || '');
        }
      } catch { setReceiptNo(''); }
    })();
  }, [patientId]);

  // helpers
  const formatDosage = (dosage) => {
    if (!dosage || typeof dosage !== 'object') return '';
    const typeLabel = DOSAGE_TYPES.find((t) => t.value === dosage.type)?.label || dosage.type || '';
    const freqLabel = dosage.frequency === 'custom'
      ? (dosage.custom || 'Custom')
      : (DOSAGE_FREQUENCIES.find((f) => f.value === dosage.frequency)?.label || dosage.frequency || '');
    const qty = dosage.quantity !== '' && dosage.quantity !== null && dosage.quantity !== undefined ? dosage.quantity : '';
    const plural = Number(qty) > 1 ? 's' : '';
    return `${qty} ${typeLabel}${typeLabel ? plural : ''}${typeLabel ? ',' : ''} ${freqLabel}`;
  };

  const handlePrint = (presc) => {
    const printWindow = window.open('', '', 'width=800,height=600');
    printWindow.document.write('<html><head><title>Prescription Print</title>');
    printWindow.document.write(
      '<style>body{font-family:sans-serif;} h2{color:var(--primary-color);} h4{color:#025D74;} table{width:100%;border-collapse:collapse;} th,td{border:1px solid #ccc;padding:8px;text-align:left;} th{background:var(--primary-color);color:#fff;} </style>'
    );
    printWindow.document.write('</head><body>');
    printWindow.document.write(`<h2>Hospital Name : ${hospitalName || 'Hospital Name'}</h2>`);
    if (subHospitalName) printWindow.document.write(`<h4>${subHospitalName}</h4>`);
    if (receiptNo) printWindow.document.write(`<div><b>Receipt No:</b> ${receiptNo}</div>`);
    printWindow.document.write('<h3>Prescription</h3>');
    printWindow.document.write(`<div><b>Date:</b> ${presc.created_at ? presc.created_at.split('T')[0] : ''}</div>`);
    printWindow.document.write('<table><thead><tr><th>Medicine</th><th>Dosage</th><th>Notes</th></tr></thead><tbody>');
    (Array.isArray(presc.medicines) ? presc.medicines : []).forEach((m) =>
      printWindow.document.write(`<tr><td>${m.medicine}</td><td>${formatDosage(m.dosage)}</td><td>${m.notes || ''}</td></tr>`)
    );
    printWindow.document.write('</tbody></table>');
    printWindow.document.write('</body></html>');
    printWindow.document.close();
    printWindow.print();
  };

  const handleDownloadPDF = (presc) => {
    const doc = new jsPDF();
    doc.setFontSize(18);
    doc.setTextColor('#0A7884');
    doc.text('Prescription', 14, 18);

    doc.setFontSize(12);
    doc.setTextColor('#025D74');
    let y = 28;
    doc.text(`Hospital Name: ${hospitalName || 'Hospital Name'}`, 14, y); y += 8;
    if (subHospitalName) { doc.text(`Sub-Hospital: ${subHospitalName}`, 14, y); y += 8; }
    if (receiptNo) { doc.text(`Receipt No: ${receiptNo}`, 14, y); y += 8; }
    doc.setTextColor('#333');
    doc.text(`Date: ${presc.created_at ? presc.created_at.split('T')[0] : ''}`, 14, y); y += 10;

    autoTable(doc, {
      startY: y,
      head: [['Medicine', 'Dosage', 'Notes']],
      body: (Array.isArray(presc.medicines) ? presc.medicines : []).map((m) => [
        m.medicine,
        formatDosage(m.dosage),
        m.notes || '',
      ]),
      theme: 'striped',
      headStyles: { fillColor: [10, 120, 132], textColor: '#fff', fontStyle: 'bold' },
      bodyStyles: { fontSize: 11 },
      styles: { cellPadding: 3, font: 'helvetica' },
      margin: { left: 14, right: 14 },
      tableWidth: 'auto',
    });

    doc.setFontSize(10);
    doc.setTextColor('#888');
    doc.text(
      'This is a digitally generated prescription. Please consult your doctor for any queries.',
      14,
      doc.internal.pageSize.getHeight() - 12,
    );

    doc.save(`Prescription_${presc.created_at ? presc.created_at.split('T')[0] : ''}.pdf`);
  };

  const sendPushNotificationToPatient = async () => {
    try {
      const deviceRes = await axios.get(`${BASE_URL}/api/patient/device-token/${patientId}`);
      const deviceToken = deviceRes.data?.device_token;
      if (!deviceToken) return;
      await axios.post(`${BASE_URL}/api/notifications/sendPush`, {
        device_token: deviceToken,
        title: 'New Prescription',
        body: 'A new prescription has been created for you by your doctor.',
        data: { type: 'prescription' },
      });
    } catch (err) {
      // optional: log error
    }
  };

  const onSubmit = async (values, { resetForm, setSubmitting }) => {
    const hospital_id = localStorage.getItem('hospitalId');
    const sub_hospital_id = localStorage.getItem('subHospitalId');

    if (!patientId || !hospital_id || !sub_hospital_id) {
      toast.error('Missing required patient or hospital information.');
      setSubmitting(false);
      return;
    }

    const payload = {
      patient_id: patientId,
      appointment_id: appointment?.id,
      hospital_id,
      sub_hospital_id,
      medicines: values.medicines.map((m) => ({
        ...m,
        dosage: {
          ...m.dosage,
          quantity: m.dosage.quantity === '' ? '' : Number(m.dosage.quantity),
        },
      })),
    };

    const request = editingId
      ? axios.put(`${BASE_URL}/api/patient/prescriptions/${editingId}`, payload)
      : axios.post(`${BASE_URL}/api/patient/prescriptions`, payload);

    toast
      .promise(request, {
        loading: editingId ? 'Updating prescription...' : 'Saving prescription...',
        success: editingId ? 'Prescription updated successfully!' : 'Prescription saved successfully!',
        error: (err) => err?.response?.data?.error || 'Something went wrong!',
      })
      .then(async () => {
        setEditingId(null);
        resetForm({ values: defaultValues });
        const res = await axios.get(`${BASE_URL}/api/patient/prescriptions/${patientId}`);
        setPrescriptions(Array.isArray(res.data) ? res.data : []);
        await sendPushNotificationToPatient();
      })
      .finally(() => setSubmitting(false));
  };

  const handleEdit = (presc) => {
    setEditingId(presc.id);
    if (Array.isArray(presc.medicines) && presc.medicines.length > 0) {
      setFormValues({
        medicines: presc.medicines.map((m) => ({
          medicine: m.medicine || '',
          dosage: typeof m.dosage === 'object'
            ? { type: m.dosage.type || '', quantity: m.dosage.quantity ?? '', frequency: m.dosage.frequency || '', custom: m.dosage.custom || '' }
            : { type: '', quantity: '', frequency: '', custom: '' },
          notes: m.notes || '',
        })),
      });
    } else if (presc.medicine && presc.dosage) {
      setFormValues({
        medicines: [
          {
            medicine: presc.medicine,
            dosage: typeof presc.dosage === 'object'
              ? { type: presc.dosage.type || '', quantity: presc.dosage.quantity ?? '', frequency: presc.dosage.frequency || '', custom: presc.dosage.custom || '' }
              : { type: '', quantity: '', frequency: '', custom: '' },
            notes: presc.notes || '',
          },
        ],
      });
    } else {
      setFormValues(defaultValues);
    }
  };

  const medicinesCount = useMemo(() => prescriptions.reduce((acc, p) => acc + (Array.isArray(p.medicines) ? p.medicines.length : 0), 0), [prescriptions]);

  return (
    <div className="d-flex flex-column flex-md-row">
       {role === 'patient' /* && <Sidebar />} */}
      <div className="flex-grow-1 w-100">
        <Row className="justify-content-center">
          <Col >
            <Card className="shadow-sm border-0 rounded-2">
              <Card.Body>
              {role === 'doctor' && (
                <>
                  <h6 className="fw-semibold mb-3 primary-class">Add Medicines</h6>

                  <Formik
                enableReinitialize
                initialValues={formValues}
                validationSchema={validationSchema}
                onSubmit={onSubmit}
              >
                {({ values, isSubmitting }) => (
                  <FormikForm className="bg-white rounded-2 p-3 border mb-3">
                    <FieldArray name="medicines">
                      {({ push, remove }) => (
                        <>
                          {values.medicines.map((med, idx) => (
                            <Row className="g-3 mb-3" key={idx}>
                              {/* Medicine */}
                              <Col xs={12} md={3} lg={2}>
                                <Form.Group controlId={`medicine-${idx}`}>
                                  <Form.Label className="fw-semibold small">Medicine</Form.Label>
                                  <Field name={`medicines[${idx}].medicine`}>
                                    {({ field, meta }) => (
                                      <Form.Control
                                        {...field}
                                        placeholder="Medicine name"
                                        size="sm"
                                        isInvalid={meta.touched && !!meta.error}
                                      />
                                    )}
                                  </Field>
                                </Form.Group>
                              </Col>

                              {/* Type */}
                              <Col xs={12} sm={6} md={2} lg={2}>
                                <Form.Group>
                                  <Form.Label className="fw-semibold small">Type</Form.Label>
                                  <Field name={`medicines[${idx}].dosage.type`}>
                                    {({ field, meta }) => (
                                      <Form.Select
                                        {...field}
                                        size="sm"
                                        isInvalid={meta.touched && !!meta.error}
                                      >
                                        <option value="">Select</option>
                                        {DOSAGE_TYPES.map((opt) => (
                                          <option key={opt.value} value={opt.value}>
                                            {opt.label}
                                          </option>
                                        ))}
                                      </Form.Select>
                                    )}
                                  </Field>
                                </Form.Group>
                              </Col>

                              {/* Quantity */}
                              <Col xs={12} sm={6} md={2} lg={2}>
                                <Form.Group>
                                  <Form.Label className="fw-semibold small">Qty</Form.Label>
                                  <Field name={`medicines[${idx}].dosage.quantity`}>
                                    {({ field, meta }) => (
                                      <Form.Select
                                        {...field}
                                        size="sm"
                                        isInvalid={meta.touched && !!meta.error}
                                      >
                                        <option value="">Select</option>
                                        {DOSAGE_QUANTITIES.map((q) => (
                                          <option key={q} value={q}>
                                            {q}
                                          </option>
                                        ))}
                                      </Form.Select>
                                    )}
                                  </Field>
                                </Form.Group>
                              </Col>

                              {/* Frequency */}
                              <Col xs={12} sm={6} md={3} lg={2}>
                                <Form.Group>
                                  <Form.Label className="fw-semibold small">Frequency</Form.Label>
                                  <Field name={`medicines[${idx}].dosage.frequency`}>
                                    {({ field, meta }) => (
                                      <Form.Select
                                        {...field}
                                        size="sm"
                                        isInvalid={meta.touched && !!meta.error}
                                      >
                                        <option value="">Select</option>
                                        {DOSAGE_FREQUENCIES.map((opt) => (
                                          <option key={opt.value} value={opt.value}>
                                            {opt.label}
                                          </option>
                                        ))}
                                      </Form.Select>
                                    )}
                                  </Field>

                                  {/* Custom frequency input */}
                                  {values.medicines[idx]?.dosage?.frequency === "custom" && (
                                    <Field name={`medicines[${idx}].dosage.custom`}>
                                      {({ field, meta }) => (
                                        <Form.Control
                                          {...field}
                                          placeholder="Custom frequency"
                                          size="sm"
                                          className="mt-2"
                                          isInvalid={meta.touched && !!meta.error}
                                        />
                                      )}
                                    </Field>
                                  )}
                                </Form.Group>
                              </Col>

                              {/* Notes */}
                              <Col xs={12} sm={6} md={2} lg={2}>
                                <Form.Group>
                                  <Form.Label className="fw-semibold small">Notes</Form.Label>
                                  <Field name={`medicines[${idx}].notes`}>
                                    {({ field, meta }) => (
                                      <Form.Control
                                        {...field}
                                        placeholder="Notes"
                                        size="sm"
                                        isInvalid={meta.touched && !!meta.error}
                                      />
                                    )}
                                  </Field>
                                </Form.Group>
                              </Col>

                              {/* Remove button */}
                              <Col
                                xs={12}
                                lg={2}
                                className="d-flex align-items-end justify-content-end"
                              >
                                <Button
                                  variant="light"
                                  size="sm"
                                  onClick={() => remove(idx)}
                                  disabled={values.medicines.length === 1}
                                  title="Remove medicine"
                                >
                                  <span
                                    className="material-icons"
                                    style={{ fontSize: 18, color: "red" }}
                                  >
                                    delete
                                  </span>
                                </Button>
                              </Col>
                            </Row>
                          ))}

                          {/* Add button */}
                          <div className="d-flex gap-2 mb-3 flex-wrap">
                            <Button
                              size="sm"
                              variant="outline-primary"
                              onClick={() =>
                                push({
                                  medicine: "",
                                  dosage: { type: "", quantity: "", frequency: "", custom: "" },
                                  notes: "",
                                })
                              }
                            >
                              + Add Medicine
                            </Button>
                          </div>
                        </>
                      )}
                    </FieldArray>

                    {/* Submit */}
                    <div className="d-flex gap-2 mt-3 flex-wrap">
                      <Button type="submit" className="btn-custom" disabled={isSubmitting}>
                        {editingId ? "Update" : "Save"}
                      </Button>
                    </div>
                  </FormikForm>
                )}
              </Formik>

                </>
              )}



                <h5 className="fw-bold mb-3 primary-class">Prescription History</h5>
                <div className="table-responsive">
                  <Table striped bordered hover responsive className="mb-0">
                    <thead>
                      <tr>
                        <th>Medicines</th>
                        <th>Dosages</th>
                        <th>Notes</th>
                        <th>Date</th>
                        <th>Download</th>
                        {role === 'doctor' && <th className="min-w-200">Actions</th>}
                      </tr>
                    </thead>
                    <tbody>
                      {prescriptions.length === 0 && (
                        <tr>
                          <td colSpan={role === 'doctor' ? 6 : 5} className="text-center text-muted">No prescriptions found.</td>
                        </tr>
                      )}
                      {prescriptions.map((p) => (
                        <tr key={p.id}>
                          <td>
                            {Array.isArray(p.medicines)
                              ? p.medicines.map((m, i) => <div key={i}>{m.medicine}</div>)
                              : p.medicine}
                          </td>
                          <td>
                            {Array.isArray(p.medicines)
                              ? p.medicines.map((m, i) => <div key={i}>{formatDosage(m.dosage)}</div>)
                              : formatDosage(p.dosage)}
                          </td>
                          <td>
                            {Array.isArray(p.medicines)
                              ? p.medicines.map((m, i) => <div key={i}>{m.notes}</div>)
                              : p.notes}
                          </td>
                          <td>{p.created_at ? p.created_at.split('T')[0] : ''}</td>
                          <td>
                            <Button
                              size="sm"
                              variant="outline-primary"
                              onClick={() => handleDownloadPDF(p)}
                            >
                              Download PDF
                            </Button>
                          </td>
                          {role === 'doctor' && (
                            <td>
                              <div className="d-flex flex-wrap gap-2">
                                <Button size="sm" variant="outline-primary" onClick={() => handleEdit(p)}>
                                  Edit
                                </Button>
                                <Button size="sm" variant="outline-success" onClick={() => handlePrint(p)}>
                                  Print
                                </Button>
                              </div>
                            </td>
                          )}
                        </tr>
                      ))}
                    </tbody>
                  </Table>
                </div>
              </Card.Body>
              <Card.Footer className="d-flex justify-content-between small text-muted">
                <span>Total prescriptions: {prescriptions.length}</span>
                <span>Total medicines prescribed: {medicinesCount}</span>
              </Card.Footer>
            </Card>
          </Col>
        </Row>
      </div>
    </div>
  );
};

export default Prescription;
